import { Container, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { PlotGroupGroupByEnum } from '@/api/rest/resources/types/plotGroup';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { usePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useGetActivePlotGroup } from '@/components/widgets/config';
import { LAYER_OPTIONS } from '@/components/widgets/o/Map/constants';
import { useMapLayers } from '@/components/widgets/o/Map/hooks/useMapLayers';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useWidgetSlotConfigurationByNameType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { isNullish } from '@/utils/booleans';

import { CreatePlotGroup } from '../components/plot-group/CreatePlotGroup';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useMenuItems } from '../hooks/useMenuItems';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { ProjectsMap } from '../projects/components/projectsMap/ProjectsMap';
import { PlotList } from './components/PlotList';

export const PlotsPage = () => {
  const { t } = useTranslation();
  const menuItems = useMenuItems();
  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);

  const organizationId = useOrganizationId();
  const showGroupByOptions = !useUIStyle(OrganizationUIStyle.CONSERVATION);

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();

  const [searchParams] = useSearchParams();
  const plotGroupId = searchParams.get('plotGroupId');

  const activePlotGroup = usePlotGroup();
  const activeFilters = useGetActivePlotGroup();
  const isFiltersSelected = activeFilters?.length > 0;
  const hasGroupByFilters = activeFilters?.some((filter) => Object.keys(PlotGroupGroupByEnum).includes(filter.key));

  // Clear stale selections when all filters are removed. When switching between
  // filters (both truthy), PlotList's defaultRowSelected effect handles re-selection.
  useEffect(() => {
    if (!isFiltersSelected) {
      setSelectedPlotIds([]);
    }
  }, [isFiltersSelected]);

  const mapLayers = useMapLayers(LAYER_OPTIONS);

  const widgetSlotsConfiguration = useWidgetSlotConfigurationByNameType(
    DefaultWidgetNames.default_template_portfolio,
    WidgetType.t_page,
  );
  return (
    <SignedInPageLayout
      items={menuItems}
      withCustomContentContainer
      navBarProps={{
        title: t('navbar.portfolio'),
        showFilters: true,
        children:
          !isLimitedAccessUser && isOrganizationMember && isNullish(plotGroupId) && showGroupByOptions ? (
            <Stack direction='row' className='justify-end gap-4'>
              <CreatePlotGroup
                key='filtered-group'
                organizationId={organizationId}
                plotGroup={activePlotGroup}
                plotIds={selectedPlotIds}
                disabled={!isFiltersSelected || false}
              />
            </Stack>
          ) : null,
      }}
    >
      <Container gutterWidth={{ sm: 4 }} className='gap-10' data-testid='plot-list-page'>
        <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
        <div data-testid='project-map-wrapper' className='h-[60vh] animate-in fade-in'>
          <ProjectsMap setSelectedPlotIds={setSelectedPlotIds} selectedPlotIds={selectedPlotIds} layers={mapLayers} />
        </div>
        {hasGroupByFilters ? (
          <PlotList />
        ) : (
          <PlotList
            enableRowSelection={true}
            defaultRowSelected={isFiltersSelected}
            setSelectedPlotIds={setSelectedPlotIds}
            selectedPlotIds={selectedPlotIds}
          />
        )}
      </Container>
    </SignedInPageLayout>
  );
};
